import React from 'react'
import { useTranslation } from 'react-i18next'

import { ClinTheme } from '../../../../ClinTheme'
import { ClinText } from '../../../../components/ClinText'
import { OrderDto, OrderLineDto } from '../../../../types/swaggerTypes'
import { StyledSummaryContainer } from './OrderSummary.styles'

import { Col, Row } from 'react-grid-system'
import { ClinSpacer } from '../../../../components/ClinSpacer'
import { numberToCurrencyString } from '../../../../utils/numberToCurrencyString'
import { getBrowserLocale } from '../../../../utils/getBrowserLocale'
import { ClinIconPathName } from '../../../../components/ClinIcon/ClinIcon.paths'
import { ClinIcon } from '../../../../components/ClinIcon'
import { getAdditionalLineItems } from '../OrderDetail.model'

interface OrderSummaryProps {
  order: OrderDto
  isMAOrder?: boolean
}

const OrderSummary: React.FC<OrderSummaryProps> = (props) => {
  const { t } = useTranslation()
  const { order, isMAOrder } = props

  const additionalLineItems: OrderLineDto[] = getAdditionalLineItems(order)

  const updateOrderLineDescription = (itemDescription: string) => {
    if (itemDescription.includes('Standard Coldchain Data Logger')) {
      return t('opa_checkout:additional_lines.data_logger_cold')
    } else if (itemDescription.includes('Standard Ambient Data Logger')) {
      return t('opa_checkout:additional_lines.data_loger_ambient')
    }
    return itemDescription
  }
  const getProductsPackPrices = (): number => {
    if (!order?.lines) return 0

    const total = order.lines
      .filter((line) => line.medicationFlag === 'Y')
      .reduce((sum, line) => {
        const price = Number(line.unitSellingPrice)
        const quantity = line.quantities.ordered
        return !isNaN(price) ? sum + price * quantity : sum
      }, 0)

    return total
  }

  return (
    <StyledSummaryContainer>
      <Row justify="between">
        <Col width="auto">
          <ClinText fontSize={18} fontWeight={500}>
            {t('order_detail:order_summary')}
          </ClinText>
        </Col>
        <Col width="auto">
          <ClinIcon
            iconName={ClinIconPathName.ChevronDown}
            iconFill={ClinTheme.colors.primaryMid}
            iconSize={16}
          />
        </Col>
      </Row>
      <ClinSpacer height={ClinTheme.space[4]} />
      <Row justify="between" style={{ flexFlow: 'nowrap' }}>
        <Col width="auto" style={{ overflow: 'hidden' }}>
          <ClinText
            color={ClinTheme.colors.black}
            fontWeight={ClinTheme.fontWeights.normal}
            fontSize={14}
            width="208px"
          >
            {t('order_detail:products_summary_total')}
          </ClinText>
        </Col>
        <Col width="auto" style={{ flexShrink: 0 }}>
          <ClinText
            color={ClinTheme.colors.black}
            fontWeight={ClinTheme.fontWeights.medium}
            fontSize={14}
            whiteSpace="nowrap"
          >
            {numberToCurrencyString(
              getProductsPackPrices(),
              getBrowserLocale(),
              {
                style: 'currency',
                currency: order.currencyCode ? order.currencyCode : 'GBP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }
            )}
          </ClinText>
        </Col>
      </Row>
      <ClinSpacer height={ClinTheme.space[2]} />
      {!isMAOrder &&
        additionalLineItems.map((additionalItem) => (
          <div key={`order-line-item-other-${additionalItem.lineId}`}>
            <Row justify="between" style={{ flexFlow: 'nowrap' }}>
              <Col width="auto">
                <ClinText
                  color={ClinTheme.colors.black}
                  fontWeight={ClinTheme.fontWeights.normal}
                  fontSize={14}
                >
                  {/* TODO: Requirements unclear as to whether to show category and or quantity */}
                  {/*{`${additionalItem.quantities.ordered} x ${additionalItem.skuCatalogItem?.item.itemCategoryCode}`}*/}
                  {/*<br />*/}
                  {updateOrderLineDescription(additionalItem.itemDescription)}
                </ClinText>
              </Col>
              <Col
                width="auto"
                className={`sub-total-${additionalItem.lineId}`}
              >
                <ClinText
                  color={ClinTheme.colors.black}
                  fontWeight={ClinTheme.fontWeights.medium}
                  fontSize={14}
                >
                  {numberToCurrencyString(
                    additionalItem.totals.subTotal,
                    getBrowserLocale(),
                    {
                      style: 'currency',
                      currency: order.currencyCode ? order.currencyCode : 'GBP',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }
                  )}
                </ClinText>
              </Col>
            </Row>
            <ClinSpacer height={ClinTheme.space[2]} />
          </div>
        ))}
      <Row justify="between">
        <Col width="auto">
          <ClinText
            color={ClinTheme.colors.black}
            fontWeight={ClinTheme.fontWeights.normal}
            fontSize={14}
          >
            {order.currencyCode === 'GBP'
              ? t('order_detail:vat')
              : t('order_detail:tax')}
          </ClinText>
        </Col>
        <Col width="auto" className="tax">
          <ClinText
            color={ClinTheme.colors.black}
            fontWeight={ClinTheme.fontWeights.medium}
            fontSize={14}
          >
            {numberToCurrencyString(order.totals.tax, getBrowserLocale(), {
              style: 'currency',
              currency: order.currencyCode,
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </ClinText>
        </Col>
      </Row>
      <ClinSpacer height={ClinTheme.space[4]} />
      <Row justify="between" align="center">
        <Col width="auto">
          <ClinText
            color={ClinTheme.colors.black}
            fontWeight={ClinTheme.fontWeights.medium}
            fontSize={18}
          >
            {t('order_detail:total')}
          </ClinText>
        </Col>
        <Col width="auto" className="total">
          <ClinText
            color={ClinTheme.colors.black}
            fontWeight={ClinTheme.fontWeights.medium}
            fontSize={ClinTheme.fontSizes[4]}
          >
            {numberToCurrencyString(order.totals.total, getBrowserLocale(), {
              style: 'currency',
              currency: order.currencyCode ? order.currencyCode : 'GBP',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </ClinText>
        </Col>
      </Row>
    </StyledSummaryContainer>
  )
}

export default OrderSummary
